import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';
import config from '../config/env';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

/**
* Provides authentication context and controls user session state for descendant components.
* @example
* AuthProvider({ children: <App /> })
* <AuthContext.Provider value={...}>...</AuthContext.Provider>
* @param {{ReactNode}} {{children}} - React components that will have access to the authentication context.
* @returns {{ReactElement}} Auth provider component that wraps its children with authentication context.
**/
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const fetchInProgress = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (!token || typeof token !== 'string') {
      setLoading(false);
      return;
    }
    
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('[Auth] Invalid token format on page load');
      localStorage.removeItem('token');
      setCurrentUser(null);
      setLoading(false);
      return;
    }
    
    try {
      const payload = JSON.parse(atob(parts[1]));
      const expiresAt = payload.exp * 1000;
      const now = Date.now();
      
      if (payload.exp != null && expiresAt < now) {
        console.warn('[Auth] Token expired on page load');
        localStorage.removeItem('token');
        setCurrentUser(null);
        setLoading(false);
        return;
      }
      
      // Token is valid, fetch user data
      console.log('[Auth] Token valid, fetching user data...');
      fetchCurrentUser(true);
    } catch (err) {
      console.error('[Auth] Token parsing error:', err);
      localStorage.removeItem('token');
      setCurrentUser(null);
      setLoading(false);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentUser = useCallback(async (silent = false, retryCount = 0) => {
    if (fetchInProgress.current) return;

    const token = localStorage.getItem('token');
    if (!token || typeof token !== 'string') {
      setCurrentUser(null);
      setLoading(false);
      return;
    }
    const parts = token.split('.');
    if (parts.length !== 3) {
      localStorage.removeItem('token');
      setCurrentUser(null);
      setLoading(false);
      return;
    }
    let payload;
    try {
      payload = JSON.parse(atob(parts[1]));
      if (payload.exp != null && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('token');
        setCurrentUser(null);
        setLoading(false);
        return;
      }
    } catch (_) {
      localStorage.removeItem('token');
      setCurrentUser(null);
      setLoading(false);
      return;
    }

    fetchInProgress.current = true;
    if (!silent) setLoading(true);
    
    console.log('[Auth] Fetching user from /api/auth/me...');
    
    try {
      const response = await api.get('/api/auth/me', {
        timeout: 15000,
        skipCache: true,
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      console.log('[Auth] Response received:', response?.status, response?.data ? 'has data' : 'no data');
      
      const userData = response?.data;
      if (userData && typeof userData === 'object' && (userData._id || userData.id)) {
        console.log('[Auth] User authenticated successfully');
        setCurrentUser(userData);
        setError('');
        if (typeof window !== 'undefined' && window.__authRetryIntervalId) {
          clearInterval(window.__authRetryIntervalId);
          window.__authRetryIntervalId = null;
        }
        return;
      }
      if (userData === null || userData === undefined) {
        console.warn('[Auth] Received null/undefined user data, retry:', retryCount);
        const nullRetries = 2;
        if (retryCount < nullRetries) {
          fetchInProgress.current = false;
          setTimeout(() => fetchCurrentUser(silent, retryCount + 1), 1000);
          return;
        }
        console.warn('[Auth] Max retries reached, logging out');
        localStorage.removeItem('token');
        setCurrentUser(null);
        if (typeof window !== 'undefined' && window.__authRetryIntervalId) {
          clearInterval(window.__authRetryIntervalId);
          window.__authRetryIntervalId = null;
        }
        return;
      }
    } catch (err) {
      console.error('[Auth] Error fetching user:', err.message, 'Status:', err.response?.status);
      
      const status = err.response?.status;
      
      // Only clear token on 401/403 if we're sure it's an auth issue, not a network/server issue
      if (status === 401 || status === 403) {
        console.warn('[Auth] Received 401/403, retry count:', retryCount);
        
        // Don't immediately logout on first failure - could be temporary server issue
        if (retryCount === 0) {
          fetchInProgress.current = false;
          setTimeout(() => fetchCurrentUser(silent, 1), 2000);
          return;
        }
        
        // After retry, if still 401/403, then logout
        console.error('[Auth] Auth failed after retry, logging out');
        localStorage.removeItem('token');
        setCurrentUser(null);
        setLoading(false);
        return;
      }
      
      const isNetworkError = err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' ||
        (err.message && String(err.message).toLowerCase().includes('network')) ||
        (err.message && String(err.message).toLowerCase().includes('timeout'));
      if (isNetworkError) {
        const maxRetries = 3;
        const delays = [2000, 4000, 6000];
        if (retryCount < maxRetries) {
          fetchInProgress.current = false;
          setTimeout(() => fetchCurrentUser(silent, retryCount + 1), delays[retryCount] ?? 6000);
          return;
        }
        if (typeof window !== 'undefined' && !window.__authRetryIntervalId) {
          window.__authRetryIntervalId = setInterval(() => {
            fetchCurrentUser(true).catch(() => {});
          }, 12000);
        }
        // Don't logout on network errors - keep user logged in
        setLoading(false);
        return;
      }
      
      // For other errors, don't logout - just stop loading
      setLoading(false);
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  }, []);

  const register = async (username, email, password) => {
    try {
      const response = await api.post('/api/auth/register', { username, email, password });
      const data = response?.data;
      const responseData = data?.data || data;
      const token = responseData?.token;
      const user = responseData?.user;
      if (!token || !user) throw new Error('Invalid response from server');
      localStorage.setItem('token', token);
      setCurrentUser(user);
      setError('');
      await new Promise(resolve => setTimeout(resolve, 50));
      return user;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Registration failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const login = async (emailOrUsername, password) => {
    try {
      const response = await api.post('/api/auth/login', { emailOrUsername, password });
      const data = response?.data;
      const responseData = data?.data || data;
      const token = responseData?.token;
      const user = responseData?.user;
      if (!token || !user) throw new Error('Invalid response from server');
      
      // Store token
      localStorage.setItem('token', token);
      
      // Update state
      setCurrentUser(user);
      setError('');
      
      // Small delay to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 50));
      return user;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const logout = () => {
    setCurrentUser(null);
    setError('');
    localStorage.removeItem('token');
    sessionStorage.clear();
    window.__redirecting = false;
    if (typeof window !== 'undefined' && window.__authRetryIntervalId) {
      clearInterval(window.__authRetryIntervalId);
      window.__authRetryIntervalId = null;
    }
    // Always redirect to home page and force refresh to clear state
    setTimeout(() => {
      window.location.href = '/';
    }, 100);
  };

  const googleLogin = () => {
    window.location.href = `${config.apiUrl}/api/auth/google`;
  };

  const githubLogin = () => {
    window.location.href = `${config.apiUrl}/api/auth/github`;
  };

  const updateCurrentUser = (updates) => {
    setCurrentUser(prev => ({ ...prev, ...updates }));
  };

  const value = { 
    currentUser, 
    loading, 
    error, 
    register, 
    login, 
    logout, 
    googleLogin, 
    githubLogin, 
    fetchCurrentUser, 
    setCurrentUser,
    updateCurrentUser 
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;